import React, { useEffect, useState, useContext } from "react";
import { Helmet } from "react-helmet-async";
import { getOrders } from "../../utils/api";
import { toast } from "react-toastify";
import axios from "axios";
import CustomToastContent from "../../components/CustomToastContent";
import SuccessIcon from "../../images/success_icon.svg";
import InfoIcon from "../../images/info_icon.svg";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { useTheme } from "../../context/ThemeContext";
import { useBuySellModalContext } from "../../context/BuySellModalContext";
import WebsocketWrapperContext from "../../context/WebsocketWrapperContext";
import { useStatus } from "../../context/StatusProvider";
import EmptyOrder from "./components/EmptyOrder";
import SearchIcon from "../../images/orders/search_icon.svg";
import OrderModal from "./components/OrdersModal";
import BuySellModal from "../../modals/BuySellModal";
import OrdersBuySellModal from "./components/OrdersBuySellModal";
import CancelConfirmationPopup from "./components/CancelOrderPopup";
import { SkeletonTable } from "./components/SkeletonTable";
import { useFundsData } from "../../context/FundsDataContext";
import { getCookie } from "../../utils/cookieUtils";

let triggerSubscribe = true;
let connectionEstablised = false;

const OrdersPage = () => {
  const { setBuySellModalData } = useBuySellModalContext();
  const { theme } = useTheme();
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const access_token = getCookie("access_token");
  const broker = localStorage.getItem("broker");
  const client_id = getCookie("client_id");
  // this state is added to keep the track of checkbox
  const [checkboxEnabled, setCheckboxEnabled] = useState(false);
  const [selectedCheckboxes, setSelectedCheckboxes] = useState([]);
  const [orderList, setOrderList] = useState([]);
  const [visibility, setVisibility] = useState(false);
  const [hoveredRowIndex, setHoveredRowIndex] = useState(null);
  const [symbols, setSymbols] = useState([]);
  const { status } = useStatus(false);
  const [searchTerm, setSearchTerm] = useState("");
  // New state for the modal
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isBuySellModalOpen, setIsBuySellModalOpen] = useState(false);
  // State for dropdown visibility
  const [openDropdownIndex, setOpenDropdownIndex] = useState(null);
  // State for master checkbox
  const [masterCheckboxState, setMasterCheckboxState] = useState(false);

  const [showBulkCancelPopup, setShowBulkCancelPopup] = useState(false);
  const [singleOrderToCancel, setSingleOrderToCancel] = useState(null);
  const { fundsData, fetchBalanceAPI } = useFundsData();

  const {
    websocketConnection,
    checkWebsocketConnection,
    subscribeToSymbols,
    unsubscribeToSymbols,
    symbolData,
  } = useContext(WebsocketWrapperContext);

  const handleBuySellModalClose = () => {
    setIsBuySellModalOpen(false);
  };
  const subscribeToOrderSymbols = (orders) => {
    if (!orders || orders.length === 0) return;

    // Extract unique symbols with their exchanges
    const symbolsByExchange = {};

    orders.forEach((order) => {
      const exchange = order.exchange || "NFO"; // Default to NFO if no exchange
      if (!symbolsByExchange[exchange]) {
        symbolsByExchange[exchange] = [];
      }
      // Add symbol code if not already present
      if (!symbolsByExchange[exchange].includes(order.code)) {
        symbolsByExchange[exchange].push(order.code);
      }
    });

    // Subscribe to symbols for each exchange
    Object.keys(symbolsByExchange).forEach((exchange) => {
      const symbols = symbolsByExchange[exchange];
      if (symbols.length > 0) {
        subscribeToSymbols(symbols, exchange);
      }
    });
  };

  const fetchOrders = async () => {
    try {
      const response = await getOrders(access_token, client_id, broker);
      if (response) {
        setOrders(response);
        //state tracking
        setSymbols(response.map((order) => order.code));

        // Subscribe to symbols immediately after fetching orders
        subscribeToOrderSymbols(response);
      } else {
        setOrders([]);
        setSymbols([]);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching orders:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (status || loading) {
      fetchOrders();
    }
  }, [status, loading]);

  useEffect(() => {
    connectionEstablised = checkWebsocketConnection();

    // Only subscribe if we have a connection and orders
    if (connectionEstablised && orders.length > 0) {
      subscribeToOrderSymbols(orders);
    }
  }, [orders, websocketConnection]);

  // cleanup
  useEffect(() => {
    return () => {
      if (symbols.length > 0) {
        const symbolsByExchange = {};

        orders.forEach((order) => {
          const exchange = order.exchange || "NFO";
          if (!symbolsByExchange[exchange]) {
            symbolsByExchange[exchange] = [];
          }
          if (!symbolsByExchange[exchange].includes(order.code)) {
            symbolsByExchange[exchange].push(order.code);
          }
        });

        // Unsubscribe from each exchange
        Object.keys(symbolsByExchange).forEach((exchange) => {
          const exchangeSymbols = symbolsByExchange[exchange];
          if (exchangeSymbols.length > 0) {
            unsubscribeToSymbols(exchangeSymbols, exchange);
          }
        });
      }
    };
  }, []);

  const formatDateTime = (dateTimeString) => {
    const dateTime = new Date(dateTimeString);
    const time = dateTime.toLocaleTimeString([], {
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
      hour12: true,
    });
    const date = dateTime.toLocaleDateString("en-US", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });

    return `${time}, ${date}`;
  };

  const filterOrdersBySearch = (orders) => {
    if (!searchTerm) return orders;
    return orders.filter((order) =>
      order.symbol.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const filteredOrders = filterOrdersBySearch(
    orders.filter((order) => {
      if (filter === "executed" && order.status !== "Executed") {
        return false;
      }
      if (
        filter === "pending" &&
        !(order.status === "Pending" || order.status === "Trigger pending")
      ) {
        return false;
      }
      if (filter === "rejected" && order.status !== "Rejected") {
        return false;
      }
      return true;
    })
  );

  const isActiveButton = (buttonName) => {
    return filter === buttonName ? "active" : "";
  };

  useEffect(() => {
    const hasPendingOrders = filteredOrders.some(
      (order) =>
        order.status === "Pending" || order.status === "Trigger pending"
    );
    setCheckboxEnabled(hasPendingOrders);
  }, [filteredOrders]);

  // Master checkbox handler
  const handleMasterCheckboxChange = () => {
    const enabledOrderIds = filteredOrders
      .filter(
        (order) =>
          order.status === "Pending" || order.status === "Trigger pending"
      )
      .map((order) => order.id);

    if (masterCheckboxState) {
      // If currently checked, uncheck all
      setSelectedCheckboxes([]);
      setOrderList([]);
      setMasterCheckboxState(false);
    } else {
      // If currently unchecked, check all enabled
      setSelectedCheckboxes(enabledOrderIds);
      const enabledOrders = filteredOrders.filter(
        (order) =>
          order.status === "Pending" || order.status === "Trigger pending"
      );
      setOrderList(enabledOrders);
      setMasterCheckboxState(true);
    }
  };

  // Update master checkbox state when individual checkboxes change
  useEffect(() => {
    const enabledOrderIds = filteredOrders
      .filter(
        (order) =>
          order.status === "Pending" || order.status === "Trigger pending"
      )
      .map((order) => order.id);

    if (enabledOrderIds.length === 0) {
      setMasterCheckboxState(false);
    } else if (
      selectedCheckboxes.length === enabledOrderIds.length &&
      enabledOrderIds.every((id) => selectedCheckboxes.includes(id))
    ) {
      setMasterCheckboxState(true);
    } else {
      setMasterCheckboxState(false);
    }
  }, [selectedCheckboxes, filteredOrders]);

  const handleCheckboxChange = (orderId) => {
    const order = orders.find((order) => order.id === orderId);
    if (
      (order && order.status === "Pending") ||
      order.status === "Trigger pending"
    ) {
      const isChecked = selectedCheckboxes.includes(orderId);
      if (isChecked) {
        setSelectedCheckboxes(
          selectedCheckboxes.filter((id) => id !== orderId)
        );
        setOrderList(orderList.filter((id) => id !== orderId));
      } else {
        setSelectedCheckboxes([...selectedCheckboxes, orderId]);
        setOrderList([...orderList, order]);
      }
    }
  };

  const handleCancelSelected = async () => {
    try {
      if (selectedCheckboxes.length > 0) {
        const response = await axios.post(
          "https://9pmtao5xc1.execute-api.ap-south-1.amazonaws.com/default/og_allbrokers_cancelorders",
          {
            broker: broker,
            access_token: access_token,
            client_id: client_id,
            order_list: orderList,
          }
        );

        if (response.data.status) {
          const updatedOrders = await getOrders(
            access_token,
            client_id,
            broker
          );
          setOrders(updatedOrders);
        } else {
          toast.error(
            <CustomToastContent
              heading="Failed to cancel orders!"
              message={response.data.message}
            />,
            {
              className: "custom-error-toast",
              icon: <img src={InfoIcon} alt="Success Icon" />,
            }
          );
        }
      } else {
        // console.log("No orders selected to cancel.");
      }
    } catch (error) {
      console.error("Error cancelling orders:", error);
      return "";
    } finally {
      setSelectedCheckboxes([]);
      setOrderList([]);
    }
  };

  // Function to handle cancellation of orders from the modal
  const handleCancelOrders = async (ordersToCancel) => {
    try {
      const response = await axios.post(
        "https://9pmtao5xc1.execute-api.ap-south-1.amazonaws.com/default/og_allbrokers_cancelorders",
        {
          broker: broker,
          access_token: access_token,
          client_id: client_id,
          order_list: ordersToCancel,
        }
      );
      fetchBalanceAPI();
      if (response.data.status) {
        const updatedOrders = await getOrders(access_token, client_id, broker);
        setOrders(updatedOrders);
        return true;
      } else {
        toast.error(
          <CustomToastContent
            heading="Failed to cancel orders!"
            message={response.data.message}
          />,
          {
            className: "custom-error-toast",
            icon: <img src={InfoIcon} alt="Success Icon" />,
          }
        );
        return false;
      }
    } catch (error) {
      console.error("Error cancelling orders:", error);
      return false;
    }
  };

  // Function to cancel a single order from dropdown
  const handleCancelSingleOrder = async (order) => {
    try {
      const response = await axios.post(
        "https://9pmtao5xc1.execute-api.ap-south-1.amazonaws.com/default/og_allbrokers_cancelorders",
        {
          broker: broker,
          access_token: access_token,
          client_id: client_id,
          order_list: [order],
        }
      );
      fetchBalanceAPI();
      if (response.data.status) {
        const updatedOrders = await getOrders(access_token, client_id, broker);
        setOrders(updatedOrders);
        toast.success(
          <CustomToastContent
            heading="Order cancelled successfully!"
            message="Order has been cancelled successfully!"
          />,
          {
            className: "custom-success-toast",
            icon: <img src={SuccessIcon} alt="Success Icon" />,
          }
        );
      } else {
        toast.error(
          <CustomToastContent
            heading="Failed to cancel order!"
            message={response.data.message}
          />,
          {
            className: "custom-error-toast",
            icon: <img src={InfoIcon} alt="Success Icon" />,
          }
        );
      }
    } catch (error) {
      console.error("Error cancelling order:", error);
      toast.error(
        <CustomToastContent
          heading="Failed to cancel order!"
          message="An error occurred while cancelling the order."
        />,
        {
          className: "custom-error-toast",
          icon: <img src={InfoIcon} alt="Success Icon" />,
        }
      );
    }
  };

  const handleRowMouseEnter = (index) => {
    setVisibility(true);
    setHoveredRowIndex(index);
  };

  const handleRowMouseLeave = () => {
    setVisibility(false);
    setHoveredRowIndex(null);
  };

  const handleModifyClick = (trade) => {
    const ltpPrice = symbolData[trade?.code]?.ltp;
    const trade_data = {
      side: trade.side ? "Buy" : "Sell",
      instrumentName: trade.symbol,
      lotSize: trade.qty / trade.lotSize,
      lot_size: trade.lotSize,
      productType: trade.productType,
      code: trade.code,
      price: trade.tradedPrice || 0,
      trigger_price: trade.trigger_price || 0,
      buy_sell: trade.isBuy ? "S" : "B",
      tradingSymbol: trade.symbol,
      quantity: trade.qty,
      exchange: trade.exchange,
      orderStatus: trade.status,
      scrip: trade.scrip,
      isModify: true,
      orderType:
        trade.orderType.toUpperCase() === "MARKET"
          ? "MARKET"
          : trade.trigger_price === "00.00" &&
              trade.orderType.toUpperCase() === "LIMIT"
            ? "LIMIT"
            : "TRIGGER",
      orderId: trade.id,
    };
    setBuySellModalData([trade_data]);
    setIsBuySellModalOpen(true);
    // console.log("tarde_dta", trade_data);
  };

  // const repeatOrderClick = (trade) => {
  //   const ltpPrice = symbolData[trade?.code]?.ltp;
  //   const trade_data = {
  //     side: trade.side ? "Buy" : "Sell",
  //     instrumentName: trade.symbol,
  //     lotSize: trade.qty / trade.lotSize,
  //     lot_size: trade.lotSize,
  //     productType: trade.productType,
  //     code: trade.code,
  //     price: trade.tradedPrice || 0,
  //     trigger_price: trade.trigger_price || 0,
  //     buy_sell: trade.isBuy ? "S" : "B",
  //     tradingSymbol: trade.symbol,
  //     quantity: trade.qty,
  //     exchange: trade.exchange,
  //     orderStatus: trade.status,
  //     orderType:
  //       trade.orderType.toUpperCase() === "MARKET"
  //         ? "MARKET"
  //         : trade.trigger_price === "00.00" &&
  //             trade.orderType.toUpperCase() === "LIMIT"
  //           ? "LIMIT"
  //           : "TRIGGER",
  //     orderId: trade.id,
  //   };
  //   setBuySellModalData([trade_data]);
  //   setIsBuySellModalOpen(true);
  //   // console.log("repeat", trade_data);
  // };

  // Handle bulk cancellation
  const handleBulkCancelConfirm = async () => {
    setShowBulkCancelPopup(false);
    try {
      await handleCancelSelected();
      fetchBalanceAPI();
      toast.success(
        <CustomToastContent
          heading="Orders cancelled successfully!"
          message="Successfully cancelled all selected orders!"
        />,
        {
          className: "custom-success-toast",
          icon: <img src={SuccessIcon} alt="Success Icon" />,
        }
      );
    } catch (error) {
      console.error("Error handling cancel operation:", error);
    }
  };

  // Handle single order cancellation
  const handleSingleCancelConfirm = async () => {
    if (!singleOrderToCancel) return;

    try {
      await handleCancelSingleOrder(singleOrderToCancel);
      toast.success(
        <CustomToastContent
          heading="Order cancelled successfully!"
          message="Order has been cancelled successfully!"
        />,
        {
          className: "custom-success-toast",
          icon: <img src={SuccessIcon} alt="Success Icon" />,
        }
      );
    } catch (error) {
      console.error("Error cancelling order:", error);
    } finally {
      setSingleOrderToCancel(null);
    }
  };

  // Handle dropdown menu actions
  const handleDropdownAction = (action, order, e) => {
    e.stopPropagation();
    setOpenDropdownIndex(null); // Close dropdown

    switch (action) {
      case "repeat":
        // repeatOrderClick(order);
        setSelectedOrder(order);
        setIsModalOpen(true);
        setBuySellModalData();
        break;
      case "modify":
        handleModifyClick(order);
        break;
      case "cancel":
        setSingleOrderToCancel(order);
        break;
      default:
        break;
    }
  };

  // Handle dropdown toggle
  const handleDropdownToggle = (index, e) => {
    e.stopPropagation();
    setOpenDropdownIndex(openDropdownIndex === index ? null : index);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setOpenDropdownIndex(null);
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // Handle modal close
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedOrder(null);
  };

  return (
    <>
      <Helmet>
        <title>Orders</title>
      </Helmet>
      <div className="overlay"></div>
      <div className="orders-page mt-14 max-w-8xl">
        <div className="flex justify-between">
          <div className="flex justify-between">
            <div className="flex gap-3">
              <div className="tabs tabs-boxed bg-p400 rounded h-8 justify-between w-[314px] p-[1.7px] ">
                <div
                  className={`tab h-7 text-xs font-medium  rounded  ${
                    isActiveButton("all")
                      ? "bg-n200 text-p300 font-semibold"
                      : "text-n500"
                  }`}
                  onClick={() => setFilter("all")}>
                  All
                </div>
                <div
                  className={`tab h-7 text-xs font-medium  ${
                    isActiveButton("executed")
                      ? "bg-n200 text-p300 font-semibold"
                      : "text-n500"
                  }`}
                  onClick={() => setFilter("executed")}>
                  Executed
                </div>
                <div
                  className={`tab h-7 text-xs font-medium ${
                    isActiveButton("pending")
                      ? "bg-n200 text-p300 font-semibold"
                      : "text-n500"
                  }`}
                  onClick={() => setFilter("pending")}>
                  Pending
                </div>
                <div
                  className={`tab  h-7 text-xs font-medium  ${
                    isActiveButton("rejected")
                      ? "bg-n200 text-p300 font-semibold"
                      : "text-n500"
                  }`}
                  onClick={() => setFilter("rejected")}>
                  Rejected
                </div>
              </div>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search by trading symbol"
                  className="w-[208px] h-[32px] rounded-[4px] border-none pl-8 pr-3 text-xs font-medium text-n400 focus:outline-none bg-p400"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <div className="absolute left-2 top-1/2 transform -translate-y-1/2">
                  <img src={SearchIcon} alt="search-icon" />
                </div>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              className={`h-8 w-[72px] rounded-md font-medium text-xs ${
                selectedCheckboxes.length > 0
                  ? "bg-p300 text-p400 cursor-pointer"
                  : "bg-p300 text-p400 opacity-50 cursor-not-allowed"
              }`}
              disabled={selectedCheckboxes.length === 0}
              onClick={() => setShowBulkCancelPopup(true)}>
              Cancel
            </button>
          </div>
        </div>

        {loading && orders.length === 0 ? (
          <SkeletonTable />
        ) : filteredOrders.length === 0 ? (
          <div className="bg-p400 rounded-md mt-3 h-[80vh] flex flex-col items-center justify-center">
            <EmptyOrder filter={filter} />
          </div>
        ) : (
          <div className="bg-p400 rounded-md mt-3 h-[80vh] overflow-y-scroll no-scrollbar">
            <table className="w-full">
              <thead>
                <tr className="h-12 ">
                  <td className="px-4 py-3">
                    <input
                      className="rounded border-n300"
                      type="checkbox"
                      checked={masterCheckboxState}
                      onChange={handleMasterCheckboxChange}
                      disabled={
                        !filteredOrders.some(
                          (order) =>
                            order.status === "Pending" ||
                            order.status === "Trigger pending"
                        )
                      }
                    />
                  </td>
                  <th className="px-4 py-3 text-xs font-medium text-n700 text-left">
                    Instrument
                  </th>
                  <th className="px-4 py-3 text-xs font-medium text-n700 text-left"></th>
                  <th className="px-4 py-3 text-xs font-medium text-n700 text-left">
                    Order ID
                  </th>
                  <th className="px-4 py-3 text-xs font-medium text-n700 text-left">
                    Executed Time
                  </th>
                  <th className="px-4 py-3 text-xs font-medium text-n700 text-center">
                    Type
                  </th>
                  <th className="px-4 py-3 text-xs font-medium text-n700 text-center">
                    Product
                  </th>
                  <th className="px-4 py-3 text-xs font-medium text-n700 text-center">
                    Quantity
                  </th>
                  <th className="px-4 py-3 text-xs font-medium text-n700 text-center">
                    Avg price
                  </th>
                  <th className="px-4 py-3 text-xs font-medium text-n700 text-left">
                    Status
                  </th>
                  <th className="px-4 py-3 text-xs font-medium text-n700 text-center">
                    Reason
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.map((order, index) => (
                  <tr
                    key={order.id}
                    className="h-12 border-b-2 border-n100 hover:bg-n100 transition-colors duration-200"
                    onMouseEnter={() => handleRowMouseEnter(index)}
                    onMouseLeave={handleRowMouseLeave}>
                    {/* Checkbox cell */}
                    <td
                      className="px-4 py-3"
                      onClick={(e) => e.stopPropagation()}>
                      <input
                        className="rounded border-n300"
                        type="checkbox"
                        disabled={
                          !(
                            order.status === "Pending" ||
                            order.status === "Trigger pending"
                          )
                        }
                        checked={selectedCheckboxes.includes(order.id)}
                        onChange={() => handleCheckboxChange(order.id)}
                      />
                    </td>

                    {/* Instrument cell */}
                    <td className="px-4 py-3 text-xs font-semibold text-p300 flex items-center relative">
                      {order.symbol}
                    </td>
                    <td className="relative px-4 py-3 text-xs font-medium text-p300">
                      {hoveredRowIndex === index && (
                        <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center gap-2 bg-p400 shadow-lg rounded-md px-2 py-1 border border-n200 z-10">
                          <button
                            className="px-2 py-1 text-[11px] font-medium text-p300 hover:bg-n300 hover:text-p300 rounded transition-colors duration-200"
                            onClick={(e) =>
                              handleDropdownAction("repeat", order, e)
                            }>
                            Repeat
                          </button>

                          {(order.status === "Pending" ||
                            order.status === "Trigger pending") && (
                            <>
                              <button
                                className="px-2 py-1 text-[11px] font-medium text-p300 hover:bg-[#E4BF0029] hover:text-p300 rounded transition-colors duration-200"
                                onClick={(e) =>
                                  handleDropdownAction("modify", order, e)
                                }>
                                Modify
                              </button>

                              <button
                                className="px-2 py-1 text-[11px] font-medium text-p300 hover:bg-red hover:text-p300 rounded transition-colors duration-200"
                                onClick={(e) =>
                                  handleDropdownAction("cancel", order, e)
                                }>
                                Cancel
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </td>
                    {/* Order ID cell */}
                    <td className="px-4 py-3 text-xs font-medium text-p300">
                      {order.id}
                    </td>

                    {/* Executed Time cell */}
                    <td className="px-4 py-3 text-xs font-medium text-n600">
                      {order.orderDateTime}
                    </td>

                    {/* Type cell */}
                    <td
                      className={`px-4 py-3 text-xs font-semibold text-center ${
                        order.side === -1 ? "text-[#B01431]" : "text-p150"
                      }`}>
                      {order.side === -1 ? "SELL" : "BUY"}
                    </td>

                    {/* Product cell */}
                    <td className="px-4 py-3 flex items-center justify-center">
                      <div className="h-5 w-16 bg-n100 rounded-sm flex items-center justify-center">
                        <span className="text-xs font-medium text-n700">
                          {order.productType === "Intraday" ? "MIS" : "NRML"}
                        </span>
                      </div>
                    </td>

                    {/* Quantity cell */}
                    <td className="px-4 py-3 text-xs font-medium text-n500 text-center">
                      {order.exchange === "MCX"
                        ? `${order.filledQty / order.lotSize}/${order.qty / order.lotSize}`
                        : `${order.filledQty}/${order.qty}`}
                    </td>

                    {/* Avg price cell */}
                    <td className="px-4 py-3 text-xs font-medium text-p300 text-center">
                      {order.tradedPrice}
                    </td>

                    {/* Status cell */}
                    <td className="px-4 py-3">
                      <div
                        className={`inline-flex px-2 py-1 rounded-sm text-xs font-medium ${
                          order.status === "Pending" ||
                          order.status === "Trigger pending"
                            ? "bg-[#E4BF0029] bg-opacity-10 text-[#7D6901]"
                            : order.status === "Executed"
                              ? "bg-[#12B16E29] bg-opacity-10 text-[#0F7047]"
                              : "bg-[#E94C6929] bg-opacity-10 text-[#B01431]"
                        }`}>
                        {order.status}
                      </div>
                    </td>

                    {/* Reason cell */}
                    <td
                      className="px-4 py-3 text-center"
                      onClick={(e) => e.stopPropagation()}>
                      {order.message !== "" && (
                        <div className="flex items-center justify-center">
                          <span
                            data-tooltip-id={`order-info-tooltip-${order.id}`}
                            role="button"
                            className="w-5 h-5 rounded-full border-2 border-solid border-n600 flex items-center justify-center text-xs font-medium">
                            !
                          </span>
                          {order.status === "Rejected" && (
                            <ReactTooltip
                              id={`order-info-tooltip-${order.id}`}
                              place="bottom"
                              content={order.message}
                              variant={theme === "dark" ? "light" : "dark"}
                            />
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {isBuySellModalOpen && (
          <OrdersBuySellModal
            orderData={selectedOrder}
            modalOpen={isBuySellModalOpen}
            setIsBuySellModalOpen={setIsBuySellModalOpen}
            modalClose={handleBuySellModalClose}
            isOrders={true}
            fetchOrders={fetchOrders}
          />
        )}
      </div>
      {/* OrderModal*/}
      {isModalOpen && selectedOrder && (
        <OrderModal
          order={selectedOrder}
          selectedOrders={selectedCheckboxes}
          onCancelOrders={handleCancelOrders}
          onClose={handleCloseModal}
          setIsBuySellModalOpen={setIsBuySellModalOpen}
        />
      )}
      {/* Confirmation Popups */}
      {showBulkCancelPopup && (
        <CancelConfirmationPopup
          isOpen={showBulkCancelPopup}
          onConfirm={handleBulkCancelConfirm}
          onCancel={() => setShowBulkCancelPopup(false)}
          message="Are you sure you want to cancel the selected orders?"
          detail={`Number of orders: ${selectedCheckboxes.length}`}
        />
      )}

      {singleOrderToCancel && (
        <CancelConfirmationPopup
          isOpen={!!singleOrderToCancel}
          onConfirm={handleSingleCancelConfirm}
          onCancel={() => setSingleOrderToCancel(null)}
          message="Are you sure you want to cancel this order?"
          detail={`Order ID: ${singleOrderToCancel.id}`}
        />
      )}
    </>
  );
};

export default OrdersPage;
